import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ChevronRight, X, ArrowLeft, AlertTriangle, Instagram } from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { useLang } from "@/i18n";
import { NUTRITION_FIELDS } from "@/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AllergenBadge } from "@/components/AllergenBadge";

function ProductCard({ product, index, onOpen }) {
  const { t, tl } = useLang();
  return (
    <motion.button
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onOpen(product)}
      className="group flex w-full flex-col overflow-hidden rounded-3xl bg-white text-left shadow-[0_12px_32px_rgba(243,120,30,0.14)] ring-1 ring-caneat-cream"
    >
      <div className="relative flex h-44 items-center justify-center bg-gradient-to-b from-caneat-cream to-white pt-3">
        <img
          src={resolveImage(product.image)}
          alt={tl(product.name)}
          className="h-40 w-auto object-contain drop-shadow-lg transition-transform duration-300 group-hover:scale-105"
        />
        <span
          className={`absolute right-2.5 top-2.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide ${
            product.available ? "bg-emerald-500 text-white" : "bg-zinc-400 text-white"
          }`}
        >
          {product.available ? t("available") : t("unavailable")}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="caneat-title text-xl text-caneat-ink">{tl(product.name)}</h3>
        <p className="mt-1 line-clamp-2 text-sm font-medium text-caneat-muted">{tl(product.description)}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-bold text-caneat-orange">{t("see_ingredients")}</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-caneat-orange text-white">
            <ChevronRight className="h-5 w-5" strokeWidth={2.6} />
          </span>
        </div>
      </div>
    </motion.button>
  );
}

function ProductSheet({ product, onClose, machineId }) {
  const { t, tl, lang } = useLang();
  useEffect(() => {
    if (product) {
      api.post("/public/events", {
        type: "product_view", machine_id: machineId, product_id: product.id, product_slug: product.slug,
      }).catch(() => {});
    }
  }, [product, machineId]);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="product-sheet"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            className="relative z-10 flex max-h-[92dvh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-caneat-pageCream sm:rounded-3xl"
          >
            <div className="relative flex h-56 shrink-0 items-center justify-center caneat-doodle-bg">
              <button
                data-testid="sheet-close"
                onClick={onClose}
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/25 text-white backdrop-blur-md"
              >
                <X className="h-5 w-5" strokeWidth={2.6} />
              </button>
              <img src={resolveImage(product.image)} alt={tl(product.name)} className="h-48 w-auto object-contain drop-shadow-2xl" />
            </div>

            <div className="no-scrollbar flex-1 overflow-y-auto px-5 pb-5 pt-5">
              <h2 className="caneat-title text-3xl text-caneat-ink" data-testid="sheet-title">{tl(product.name)}</h2>
              <p className="mt-2 text-sm font-medium leading-relaxed text-caneat-muted">{tl(product.description)}</p>

              {/* Composition */}
              {Array.isArray(product.composition) && product.composition.length > 0 && (
                <div className="mt-5" data-testid="composition-section">
                  <h3 className="caneat-title mb-3 text-lg text-caneat-ink">{t("composition_title")}</h3>
                  <div className="space-y-2.5">
                    {product.composition.map((c, i) => {
                      const pct = parseFloat(String(c.pct).replace(",", "."));
                      return (
                        <div key={i} data-testid={`composition-row-${i}`}>
                          <div className="mb-1 flex items-center justify-between text-sm">
                            <span className="font-semibold text-caneat-ink">{tl(c.label)}</span>
                            <span className="font-bold text-caneat-orange">{String(c.pct).replace(".", ",")} %</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-caneat-cream">
                            <div className="h-full rounded-full bg-gradient-to-r from-caneat-orangeLight to-caneat-orange"
                              style={{ width: `${Math.min(100, isNaN(pct) ? 0 : pct)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Allergens — high visibility */}
              <div className="mt-5 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="allergens-section">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-white">
                    <AlertTriangle className="h-4 w-4" strokeWidth={2.6} />
                  </span>
                  <h3 className="caneat-title text-lg uppercase tracking-wide text-amber-900">{t("allergens_title")}</h3>
                </div>
                <p className="mb-3 text-xs font-bold uppercase tracking-wide text-amber-800">{t("allergens_contains")}</p>
                <div className="flex flex-wrap gap-2">
                  {(product.allergens || []).map((a) => (
                    <AllergenBadge key={a} keyName={a} size="lg" />
                  ))}
                </div>
                {tl(product.allergens_detail) && (
                  <p className="mt-3 text-xs font-semibold leading-relaxed text-amber-900">{tl(product.allergens_detail)}</p>
                )}
              </div>

              {/* Ingredients */}
              <div className="mt-5">
                <h3 className="caneat-title mb-2 text-lg text-caneat-ink">{t("ingredients_title")}</h3>
                <p className="rounded-2xl bg-white p-4 text-sm font-medium leading-relaxed text-caneat-ink ring-1 ring-caneat-cream" data-testid="ingredients-text">
                  {tl(product.ingredients)}
                </p>
              </div>

              {/* Nutrition */}
              {product.nutrition && Object.values(product.nutrition).some((v) => v && String(v).trim()) && (
                <div className="mt-5" data-testid="nutrition-section">
                  <h3 className="caneat-title mb-2 text-lg text-caneat-ink">{t("nutrition_title")}</h3>
                  <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-caneat-cream">
                    {NUTRITION_FIELDS.filter((f) => product.nutrition[f.key] && String(product.nutrition[f.key]).trim()).map((f, i) => (
                      <div key={f.key} className={`flex items-center justify-between px-4 py-2.5 text-sm ${i % 2 ? "bg-caneat-pageCream" : "bg-white"}`}>
                        <span className={`${f.key === "saturates" || f.key === "sugars" ? "pl-3 font-medium text-caneat-muted" : "font-semibold text-caneat-ink"}`}>
                          {f[lang] || f.fr}
                        </span>
                        <span className="font-bold text-caneat-ink">
                          {product.nutrition[f.key]}{f.unit ? ` ${f.unit}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-caneat-cream bg-white p-4">
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={onClose}
                data-testid="sheet-back"
                className="flex min-h-[54px] w-full items-center justify-center gap-2 rounded-full bg-caneat-orange text-base font-extrabold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(243,120,30,0.35)]"
              >
                <ArrowLeft className="h-5 w-5" strokeWidth={2.6} /> {t("back_to_cans")}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Products() {
  const { machineId = "machine-01" } = useParams();
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/products", { params: { machine_id: machineId } });
        if (alive) setProducts(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [machineId]);

  return (
    <div className="min-h-[100dvh] bg-caneat-pageCream">
      <header className="sticky top-0 z-30 caneat-doodle-bg">
        <div className="mx-auto flex max-w-md items-center justify-between gap-2 px-4 py-4">
          <img src="/brand/caneat-logo-white.png" alt="CAN'EAT" className="h-11 w-auto shrink-0" />
          <a
            href="https://www.instagram.com/caneat.be?utm_source=ig_web_button_share_sheet&stkn=ZDNlZDc0MzIxNw=="
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-link"
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-3 py-2 text-[11px] font-extrabold uppercase tracking-wide text-white backdrop-blur-md transition-colors hover:bg-white/30"
          >
            <Instagram className="h-4 w-4" strokeWidth={2.4} />
            Check l'insta
          </a>
          <LanguageSwitcher variant="light" />
        </div>
      </header>

      <main className="mx-auto max-w-md px-5 pb-16 pt-6">
        <h1 className="caneat-title text-4xl leading-tight text-caneat-ink" data-testid="products-title">{t("choose_your")}</h1>
        <p className="mt-1 text-sm font-semibold text-caneat-muted">{t("choose_sub")}</p>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-caneat-orange" />
          </div>
        ) : products.length === 0 ? (
          <p className="mt-16 text-center text-sm font-semibold text-caneat-muted">{t("no_products")}</p>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onOpen={setActive} />
            ))}
          </div>
        )}

        <div className="mt-10 border-t border-caneat-cream pt-5 text-center">
          <a href="/admin/login" data-testid="admin-access-link"
            className="text-xs font-semibold text-caneat-muted/70 underline-offset-2 transition-colors hover:text-caneat-orange hover:underline">
            Espace admin CAN'EAT
          </a>
        </div>
      </main>

      <ProductSheet product={active} onClose={() => setActive(null)} machineId={machineId} />
    </div>
  );
}
